import { zodResolver } from '@hookform/resolvers/zod';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import Checkbox, { ICheckboxProps } from '@ui/forms/Checkbox';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { commonLabels } from '@utils/button';
import { exists } from '@utils/general';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import { schema } from './MedicareAttestationStep.data';

export interface IMedicareAttestationStepPageContent {
	pageHeader: string;
	pageInfo: string;
	medicareAttestationText: string;
}

export interface IMedicareAttestationStepProps extends IFlowStepComponentProps {
	pageContent: IMedicareAttestationStepPageContent;
}

const SpacedColumn = spaced<IFlexColumn>(FlexColumn);
const SpacedCheckbox = spaced<ICheckboxProps>(Checkbox);
const FullWidthButton = fullWidth<ISubmitButtonProps>(SubmitButton);

const MedicareAttestationStep: FC<IMedicareAttestationStepProps> = ({
	pageContent,
	existingFormData,
	handleFormSubmission = () => {},
	stepKey,
}) => {
	const [submitting, setSubmitting] = useState(false);

	const { pageHeader, pageInfo, medicareAttestationText } = pageContent;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		register,
		formState: { errors },
	} = form;

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		await handleFormSubmission(data, stepKey);
		setSubmitting(false);
	};

	return (
		<CenteredGridItem>
			<form onSubmit={handleSubmit(onSubmit)}>
				<SpacedColumn gap="sm" marginBottom="xxl">
					<HeadingText>{pageHeader}</HeadingText>
					<BasicText>{pageInfo}</BasicText>
					<SpacedCheckbox
						{...register('medicareAttestation')}
						label={medicareAttestationText}
						textSize="sm"
						marginTop="xs"
						alignment="flex-start"
						errorLabel={
							exists(errors?.medicareAttestation?.message) && errors?.medicareAttestation?.message
						}
					/>
				</SpacedColumn>
				<FullWidthButton submitting={submitting} label={commonLabels.continue} />
			</form>
		</CenteredGridItem>
	);
};

export default MedicareAttestationStep;
